import React, { useState } from 'react';
import { toast } from 'react-toastify';

import '../../css/modal.css';
import { createCustomer } from '../../api/Customer';
import { Customer, CustomerUpdate } from '../../types/Customer';
import CustomerFormFields from './CustomerFormFields';

interface CustomerCreateModalProps {
  onClose: () => void;
  onCustomerCreated: (id: number) => void;
}

const CustomerCreateModal: React.FC<CustomerCreateModalProps> = ({ onClose, onCustomerCreated }) => {
  const [formData, setFormData] = useState<Customer>({
    name: '',
    email: '',
    phoneNumber: '',
    city: '',
    address: '',
    profession: '',
    source: undefined,
    description: '',
    area: '',
    latitude: '',
    longitude: '',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    const { name, email, phoneNumber } = formData;

    if (!name || !phoneNumber) {
      toast.error('Name and Phone are required.');
      return false;
    }

    if (email && email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast.warning('Enter a valid email address.');
        return false;
      }
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(formData.phoneNumber)) {
      toast.warning("Enter a valid 10-digit phone number.");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    if (navigator.geolocation) {
      setSaving(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const updatedFormData = {
            ...formData,
            latitude: latitude.toString(),
            longitude: longitude.toString(),
            phoneNumber: `+91${formData.phoneNumber}`
          };

          try {
            const newCustomer = await createCustomer(updatedFormData);
            toast.success('Customer created successfully!');
            onCustomerCreated(newCustomer.id);
          } catch (error) {
            toast.error('Failed to create customer.');
          } finally {
            setSaving(false);
          }
        },
        () => {
          toast.error('Please allow location access to continue.');
          setSaving(false);
        }
      );
    } else {
      toast.error('Geolocation not supported by this browser.');
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div className="modal modal-lg customer-modal" role="dialog" aria-modal="true" aria-labelledby="create-customer-title" onClick={event => event.stopPropagation()}>
        <div className="modal-header">
          <h2 id="create-customer-title">Add Customer</h2>
          <button className="close-btn" onClick={onClose} aria-label="Close create customer dialog" disabled={saving}>×</button>
        </div>
        <CustomerFormFields
          formData={formData as CustomerUpdate}
          onChange={handleChange}
          phoneField={(
            <>
              <label htmlFor="customer-phone-number">Phone number *</label>
              <div className="phone-input-wrapper">
                <span className="country-code">+91</span>
                <input id="customer-phone-number" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} />
              </div>
            </>
          )}
        />
        <div className="modal-actions">
          <button className="secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Creating…' : 'Create customer'}</button>
        </div>
      </div>
    </div>
  );
};

export default CustomerCreateModal;
